import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';

import chatServiceApi from '@/core/chat/chat-keys/chat-service-api';
import {
  ChatPermissions,
  existingChatPermissions,
  Key,
  KeyRequest,
  KeyStatusEnum,
} from '@/core/chat/chat-keys/key';
import { Page } from '@/shared/pagination/export';
import { KeyForm } from '@/core/chat/chat-keys/key-form';

export default function KeyList({
  keys,
  loadKeys,
  createKey,
  userChatPermissions,
}: {
  keys: Key[];
  loadKeys: () => Promise<Page<Key>>;
  createKey: () => void;
  userChatPermissions: ChatPermissions[];
}) {
  const [editingKey, setEditingKey] = useState<KeyRequest | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<Key | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedKeyId, setExpandedKeyId] = useState<string | null>(null);

  const handleRevokeKey = async () => {
    if (!keyToRevoke) return;

    await chatServiceApi.revokeKey(keyToRevoke.id as string);
    setKeyToRevoke(null);
    setShowRevokeModal(false);
    loadKeys();
  };

  const handleSaveEdit = async () => {
    if (!editingKey) return;
    await chatServiceApi.updateKey({
      id: editingKey.id as string,
      body: {
        name: editingKey?.name,
        permissions: editingKey.permissions,
      },
    });
    setEditingKey(null);
    loadKeys();
    setShowEditModal(false);
  };

  const handleExpand = (id: string) => {
    setExpandedKeyId(expandedKeyId === id ? null : id);
  };

  const handleEditClick = (key: Key) => {
    setEditingKey({
      id: key.id as string,
      name: key.name ?? '',
      permissions: key.permissions,
    });
    setShowEditModal(true);
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setShowEditModal(false);
  };

  return (
    <div className="container-element">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold mb-0">Existing Keys</h3>
        <button className="btn btn-outline-secondary" onClick={createKey}>
          Create Key
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-borderless align-middle mb-0">
          <thead>
            <tr className="text-secondary">
              <th style={{ width: '60%' }}>Key name</th>
              <th style={{ width: '20%' }} className="text-center">
                Status
              </th>
              <th style={{ width: '20%' }}></th>
            </tr>
          </thead>
          <tbody>
            {keys.map(key => {
              const isExpanded = expandedKeyId === (key.id as string);
              return (
                <tr key={key.id as string} className="border-bottom">
                  <td className="align-middle">
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-link p-0 me-2"
                        onClick={() => handleExpand(key.id as string)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
                      </button>
                      <span className="text-truncate" style={{ maxWidth: 220 }} title={key.name}>
                        {key.name}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="ms-4 mt-2">
                        <div className="fw-semibold mb-1">Permissions</div>
                        <ul className="mb-2 ps-3">
                          {key.permissions.map(p => (
                            <li key={p}>
                              {existingChatPermissions.filter(e => e.id === p)[0]?.name || p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </td>
                  <td className="align-middle text-center">
                    <span
                      className={`badge px-3 py-2 fs-6 fw-semibold ${key.status === KeyStatusEnum.ACTIVE ? 'bg-success-subtle text-success' : key.status === KeyStatusEnum.CREATED ? 'bg-info-subtle text-info' : key.status === KeyStatusEnum.INACTIVE ? 'bg-warning-subtle text-warning' : 'bg-danger-subtle text-danger'}`}
                      style={{ borderRadius: 12 }}
                    >
                      {key.status.toString().charAt(0).toUpperCase() +
                        key.status.toString().slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td className="align-middle text-end">
                    <div className="d-flex justify-content-end gap-3">
                      <button
                        className="btn btn-link p-0"
                        onClick={() => handleEditClick(key)}
                        aria-label="Edit"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      {key.status !== KeyStatusEnum.REVOKED && (
                        <button
                          className="btn btn-link p-0"
                          onClick={() => {
                            setKeyToRevoke(key);
                            setShowRevokeModal(true);
                          }}
                          aria-label="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal show={showRevokeModal} onHide={() => setShowRevokeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Revocation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to revoke the key{' '}
          <strong>{keyToRevoke?.name || '(no name)'}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowRevokeModal(false)}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleRevokeKey}>
            Confirm Revoke
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleCancelEdit} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Key</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingKey && (
            <>
              <KeyForm
                keyPermissions={editingKey.permissions}
                setKeyPermissions={updater =>
                  setEditingKey(prev => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      permissions:
                        typeof updater === 'function' ? updater(prev.permissions) : updater,
                    } as KeyRequest;
                  })
                }
                keyName={editingKey.name || ''}
                setKeyName={name => setEditingKey(prev => ({ ...prev, name }) as KeyRequest)}
                userChatPermissions={userChatPermissions}
              />
              <button
                disabled={!editingKey.name || editingKey.permissions.length === 0}
                className="btn btn-tertiary text-base-light"
                onClick={handleSaveEdit}
              >
                Save
              </button>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
